import { useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useContext } from 'react';
import { selectClientById } from 'app/store/clientsSlice';
import { selectAllTemplates } from 'app/store/recordTemplatesSlice';
import { selectUserSettings } from 'app/store/userSettingsSlice';
import { selectOrganizationSettings, fetchOrganizationSettings } from 'app/store/organizationSettingsSlice';
import SidebarListItem from 'app/shared-components/layout/SidebarListItem';
import LeftSidebarMenu from 'app/shared-components/layout/LeftSidebarMenu';
import { buildRecordNavItems } from 'app/shared-components/client-records/utils/recordTypeUtils';
import { ClientAppContext } from 'app/shared-components/client/manage/ManageClientContent';

function ContextualSidebarContent({ closeSidebar, orgSidebarContent }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { groupId, clientId } = useParams();
  const isClientContext = location.pathname.includes('/manageclient/');
  
  // Client-specific data
  const client = useSelector(selectClientById(clientId));
  const allTemplates = useSelector(selectAllTemplates);
  const userSettings = useSelector(selectUserSettings);
  const organizationSettings = useSelector(selectOrganizationSettings(groupId));
  const { recordsEnabled } = useContext(ClientAppContext) || {};
  
  // Load organization settings if not already loaded
  useEffect(() => {
    if (groupId && !organizationSettings) {
      dispatch(fetchOrganizationSettings(groupId));
    }
  }, [dispatch, groupId, organizationSettings]);
  
  // Use organization settings in organization context, fallback to user settings
  const settings = organizationSettings || userSettings;
  
  if (!isClientContext) {
    // Show original organization sidebar
    return orgSidebarContent;
  }
  
  // Client context - show client navigation
  const recordItems = buildRecordNavItems(settings, allTemplates, clientId, recordsEnabled);
  const unread = client?.unread;
  
  return (
    <LeftSidebarMenu back={{
      link: `/apps/organization/${groupId}/clients`,
      text: "All Clients"
    }}>
      
      <SidebarListItem 
        link={`/apps/organization/${groupId}/manageclient/${clientId}`}
        key="info"
        icon="heroicons-outline:user"
        text="Client Information"
        onClick={closeSidebar}
      />

      {recordItems.map((item) => (
        item.children ? (
          <SidebarListItem
            key={item.key}
            icon={item.icon}
            text={item.text}
            expanded={false}>
            {item.children.map((child) => (
              <SidebarListItem
                link={child.link.replace('/apps/client/', `/apps/organization/${groupId}/manageclient/`)}
                key={child.key}
                icon={child.icon}
                onClick={closeSidebar}
                text={child.text}
              />
            ))}
          </SidebarListItem>
        ) : (
          <SidebarListItem
            link={item.link.replace('/apps/client/', `/apps/organization/${groupId}/manageclient/`)}
            key={item.key}
            icon={item.icon}
            onClick={closeSidebar}
            text={item.text}
          />
        )
      ))}
      
      <SidebarListItem 
        link={`/apps/organization/${groupId}/manageclient/${clientId}/teams`}
        key="teams"
        icon="material-outline:people_outline"
        text="Client Teams"
        onClick={closeSidebar}
      />
      
      <SidebarListItem 
        link={`/apps/organization/${groupId}/manageclient/${clientId}/users`}
        key="users"
        icon="heroicons-outline:user"
        text="Authorized Users"
        unread={unread}
        onClick={closeSidebar}
      />

      <SidebarListItem 
        link={`/apps/organization/${groupId}/manageclient/${clientId}/tasks`}
        key="tasks"
        icon="heroicons-outline:clipboard-list"
        text="Client Tasks"
        onClick={closeSidebar}
      />

      <SidebarListItem 
        link={`/apps/organization/${groupId}/manageclient/${clientId}/sessions`}
        key="sessions"
        icon="heroicons-outline:calendar"
        text="Sessions"
        onClick={closeSidebar}
      />

      <SidebarListItem 
        link={`/apps/organization/${groupId}/manageclient/${clientId}/files`}
        key="files"
        icon="heroicons-outline:folder"
        text="Client Files"
        onClick={closeSidebar}
      />

      <SidebarListItem 
        link={`/apps/organization/${groupId}/templates`}
        key="templates"
        icon="heroicons-outline:template"
        text="Record Templates"
        onClick={closeSidebar}
      />
    </LeftSidebarMenu>
  );
}

export default ContextualSidebarContent;